import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, Check, Ban, PauseCircle, X } from 'lucide-react';
import api from '@/services/apiClient';
import PageHeader from '@/components/ui/PageHeader';
import DataTable from '@/components/ui/DataTable';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Avatar from '@/components/ui/Avatar';
import Card from '@/components/ui/Card';
import Toolbar from '@/components/ui/Toolbar';
import Modal from '@/components/ui/Modal';
import UserDetail from './UserDetail';
import { TRAINER_STATUS } from '@/lib/enums';

const STATUS_FILTER = (t) => [
  { value: '', label: t('trainers.filter.all') },
  { value: 'pending', label: t('trainers.filter.pending') },
  { value: 'approved', label: t('trainers.filter.approved') },
  { value: 'blocked', label: t('trainers.filter.blocked') },
  { value: 'suspended', label: t('trainers.filter.suspended') },
];

const trainerName = (r) => r.name || r.user?.name || '—';
// A listagem retorna TrainerProfile (r.id = id do PERFIL); a API de status
// espera o id do USUÁRIO do personal.
const userIdOf = (r) => r.userId ?? r.user?.id ?? r.id;
const trainerCref = (r) => r.cref || r.trainerProfile?.cref;
const trainerSpecialties = (r) => {
  const s = r.specialties || r.trainerProfile?.specialties;
  if (Array.isArray(s)) return s.join(' · ');
  return s || '';
};

/** Card de aprovação no estilo do mock. */
function ApprovalCard({ trainer, busy, onApprove, onReject, t }) {
  const cref = trainerCref(trainer);
  const specialties = trainerSpecialties(trainer);
  const meta = [cref && `CREF ${cref}`, specialties].filter(Boolean).join(' · ');
  return (
    <Card style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 14 }}>
      <Avatar name={trainerName(trainer)} src={trainer.user?.avatar} size={46} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: 16,
            color: 'var(--hi)',
          }}
        >
          {trainerName(trainer)}
        </div>
        <div style={{ color: 'var(--mid)', fontSize: 11.5, marginTop: 2 }}>{meta || '—'}</div>
      </div>
      <span
        style={{
          width: 54,
          height: 38,
          borderRadius: 6,
          flex: 'none',
          background:
            'repeating-linear-gradient(45deg, var(--surf2), var(--surf2) 5px, color-mix(in srgb, var(--surf2) 60%, var(--bg)) 5px, color-mix(in srgb, var(--surf2) 60%, var(--bg)) 10px)',
        }}
        aria-hidden
        title={t('trainers.documents')}
      />
      <div style={{ display: 'flex', gap: 8, flex: 'none' }}>
        <Button
          size="sm"
          variant="outline"
          icon={X}
          loading={busy === userIdOf(trainer) + 'blocked'}
          onClick={() => onReject(userIdOf(trainer))}
          style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}
        >
          {t('screens.reject')}
        </Button>
        <Button
          size="sm"
          icon={Check}
          loading={busy === userIdOf(trainer) + 'approved'}
          onClick={() => onApprove(userIdOf(trainer))}
        >
          {t('screens.approve')}
        </Button>
      </div>
    </Card>
  );
}

export default function AdminTrainers() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get('/trainers/admin/all', status ? { status } : {})
      .then((res) => setRows((res.data || res).trainers || res.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, [status]);

  const setTrainerStatus = async (id, next) => {
    setBusy(id + next);
    try {
      await api.patch(`/trainers/admin/${id}/status`, { status: next });
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(null);
    }
  };

  const pending = rows.filter((r) => r.status === 'pending');
  const activeCount = rows.filter((r) => r.status === 'approved').length;

  const columns = [
    {
      key: 'name',
      header: t('trainers.column.trainer'),
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{trainerName(r)}</div>
          <div style={{ color: 'var(--low)', fontSize: 12 }}>{r.email || r.user?.email}</div>
        </div>
      ),
    },
    { key: 'city', header: t('trainers.column.city'), render: (r) => r.city || r.trainerProfile?.city || '—' },
    {
      key: 'rating',
      header: t('trainers.column.rating'),
      render: (r) => {
        const v = r.ratingAvg ?? r.rating;
        return v == null ? '—' : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Star size={13} fill="var(--warning)" color="var(--warning)" /> {Number(v).toFixed(1)}
          </span>
        );
      },
    },
    {
      key: 'status',
      header: t('screens.status'),
      render: (r) => {
        const s = TRAINER_STATUS[r.status] || { label: r.status, variant: 'neutral' };
        return <Badge variant={s.variant}>{s.label}</Badge>;
      },
    },
    {
      key: 'actions',
      header: t('screens.actions'),
      align: 'right',
      render: (r) => (
        // Só ícone + tooltip (padrão das tabelas) — rótulos quebravam a linha.
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', whiteSpace: 'nowrap' }}>
          {r.status !== 'approved' && (
            <Button size="sm" variant="outline" icon={Check} loading={busy === userIdOf(r) + 'approved'}
              title={t('screens.approve')} aria-label={t('screens.approve')}
              onClick={(e) => { e.stopPropagation(); setTrainerStatus(userIdOf(r), 'approved'); }} />
          )}
          {r.status !== 'suspended' && (
            <Button size="sm" variant="ghost" icon={PauseCircle} loading={busy === userIdOf(r) + 'suspended'}
              title={t('screens.suspend')} aria-label={t('screens.suspend')}
              onClick={(e) => { e.stopPropagation(); setTrainerStatus(userIdOf(r), 'suspended'); }} />
          )}
          {r.status !== 'blocked' && (
            <Button size="sm" variant="danger" icon={Ban} loading={busy === userIdOf(r) + 'blocked'}
              title={t('screens.block')} aria-label={t('screens.block')}
              onClick={(e) => { e.stopPropagation(); setTrainerStatus(userIdOf(r), 'blocked'); }} />
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader kicker="Movez · Admin" title={t('nav.trainers')}>
        <div style={{ display: 'flex', gap: 8 }}>
          <Badge variant="accent">{t('trainers.pendingCount', { count: pending.length })}</Badge>
          <Badge variant="neutral">{t('trainers.activeCount', { count: activeCount })}</Badge>
        </div>
      </PageHeader>

      {pending.length > 0 && (
        <section style={{ marginBottom: 'var(--space-6)' }}>
          <div className="kicker" style={{ marginBottom: 12 }}>
            {t('trainers.awaitingApproval')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {pending.map((tr) => (
              <ApprovalCard
                key={tr.id}
                trainer={tr}
                busy={busy}
                onApprove={(id) => setTrainerStatus(id, 'approved')}
                onReject={(id) => setTrainerStatus(id, 'blocked')}
                t={t}
              />
            ))}
          </div>
        </section>
      )}

      <Toolbar>
        <Select value={status} onChange={(e) => setStatus(e.target.value)} options={STATUS_FILTER(t)} />
      </Toolbar>
      <DataTable columns={columns} rows={rows} loading={loading}  onRowClick={(r) => setDetailId(userIdOf(r))} />
      <Modal open={!!detailId} onClose={() => setDetailId(null)} kicker={t('userDetail.kicker')} title={t('userDetail.title')} size="md">
        {detailId && <UserDetail userId={detailId} />}
      </Modal>
    </>
  );
}
